import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DentalChart from '../DentalChart/DentalChart';
import InternalHeader from '../InternalHeader';
import Sidebar from '../Sidebar';
import './PatientProfile.css';
import LinkMap from '../LinkMap/LinkMap';
import AccessControl from '../AccessControl';
import DataTable from 'react-data-table-component';
import { format } from 'date-fns';

const PatientProfile = () => {
    const { id } = useParams();
    const [patient, setPatient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('generalInfo'); // Track active tab
    const [isSidebarVisible, setIsSidebarVisible] = useState(true);
    const [isEditable, setIsEditable] = useState(false);
    const [inputValue, setInputValue] = useState('Initial Value');
    const [successModalVisible, setSucessModal] = useState(false);
    const linkData = [
        { label: 'Patients', path: '/patients' },
        { label: 'Patient Profile', path: '/patient-profile' },
    ];
    const [externalUsers, setExternalUsers] = useState([]);
    const [medicalAlertModalVisible, setMedicalAlertModalVisible] = useState(false);
    const [appointments, setAppointments] = useState([]);
    const [users, setUsers] = useState([]);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const [internalResponse, externalResponse] = await Promise.all([
                    fetch('https://api.hgcdental.clinic/api/internalusers'),
                    fetch('https://api.hgcdental.clinic/api/externalusers')
                ]);

                if (!internalResponse.ok || !externalResponse.ok)
                    throw new Error('Failed to fetch users');

                const internalUsers = await internalResponse.json();
                const externalUsers = await externalResponse.json();

                // Combine both user lists and mark their type
                const combinedUsers = [
                    ...internalUsers.map(user => ({ ...user, type: 'Internal' })),
                    ...externalUsers.map(user => ({ ...user, type: 'External' }))
                ];

                setUsers(combinedUsers);
            } catch (error) {
                console.error('Error fetching users:', error);
            }
        };

        fetchUsers();
    }, []);


    const handleSaveClick = async () => {
        try {
            // First update the patient record
            const response = await fetch(`https://api.hgcdental.clinic/api/patients/${id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(patient)
            });

            if (!response.ok) {
                throw new Error("Failed to update patient.");
            }

            const data = await response.json();
            console.log("Patient updated successfully:", data);

            // Then update the user account if one is selected
            if (patient.userAccount) {
                try {
                    await handleSave();
                } catch (error) {
                    // Show error message to user
                    alert(error.message);
                    // Optionally reload the user list to show current assignments
                    // fetchUsers();
                    return;
                }
            }

            setSucessModal(true);
            setIsEditable(false);
        } catch (error) {
            console.error("Error updating patient:", error);
            alert("Failed to update patient: " + error.message);
        }
    };

    const handleSave = async () => {
        try {
            // First check if the patientID is already assigned to another user
            const checkResponse = await fetch(`https://api.hgcdental.clinic/api/externalusers/check-patient`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    patientID: patient.patientID,
                    excludeUserId: patient.userAccount
                })
            });

            if (!checkResponse.ok) {
                const errorData = await checkResponse.json();
                throw new Error(errorData.message || "Validation failed");
            }

            // If validation passes, update the user
            const response = await fetch(`https://api.hgcdental.clinic/api/externalusers/${patient.userAccount}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ patientID: patient.patientID })
            });

            if (!response.ok) {
                throw new Error("Failed to update user.");
            }

            const data = await response.json();
            console.log("User updated successfully:", data);
            return data;
        } catch (error) {
            console.error("Error updating user:", error);
            throw error;
        }
    };

    useEffect(() => {
        const fetchExternalUsers = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/externalusers');
                if (!response.ok) {
                    throw new Error('Failed to fetch external users');
                }
                const data = await response.json();
                setExternalUsers(data); // Set the fetched data to state
            } catch (error) {
                console.error('Error fetching external users:', error);
            }
        };

        fetchExternalUsers();
    }, []);

    const toggleSidebar = () => {
        setIsSidebarVisible(!isSidebarVisible);
    };

    const handleEditClick = () => {
        setIsEditable(true);
    };

    const handleOverlayClick = (e) => {
        if (e.target.className === 'modal-overlay') {
            this.closeModal();
        }
    };

    const closeSuccessModal = () => {
        setSucessModal(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        let updatedPatient = {
            ...patient,
            [name]: value,
        };

        // If birthdate is changed, recalculate age
        if (name === 'birthdate') {
            const birth = new Date(value);
            const now = new Date();

            let years = now.getFullYear() - birth.getFullYear();
            let months = now.getMonth() - birth.getMonth();

            if (months < 0 || (months === 0 && now.getDate() < birth.getDate())) {
                years--;
                months += 12;
            }

            updatedPatient.ageYear = years;
            updatedPatient.ageMonth = months;
        }

        setPatient(updatedPatient);
    };

    useEffect(() => {
        const fetchPatient = async () => {
            const response = await fetch(`https://api.hgcdental.clinic/api/patients/${id}`);
            if (response.ok) {
                const data = await response.json();
                setPatient(data);
                if (data.MedicalAlert && data.MedicalAlert.trim() !== '') {
                    setMedicalAlertModalVisible(true);
                }

                fetchAppointments(data.name, data.email);
            } else {
                console.error('Failed to fetch patient:', response.status);
            }
            setLoading(false);
        };

        fetchPatient();
    }, [id]);

    const fetchAppointments = async (name, email) => {
        try {
            const response = await fetch('https://api.hgcdental.clinic/api/appointments');
            if (!response.ok) throw new Error('Failed to fetch appointments');
            const allAppointments = await response.json();

            // Filter based on name and email
            const patientAppointments = allAppointments.filter(
                (appt) => appt.patientName === name && appt.email === email
            );

            setAppointments(patientAppointments);
        } catch (error) {
            console.error('Error fetching appointments:', error);
        }
    };

    if (loading) return <div>Loading...</div>;
    if (!patient) return <div>No patient found</div>;

    const GeneralInfo = () => (
        <div>

            {/* <p>Chief Complaint: {patient.chiefComplaint}</p> */}
            <div className="input-group">
                <label className="label">Chief Complaint</label>
                <textarea
                    type="text"
                    name="chiefComplaint"
                    value={patient.chiefComplaint}
                    disabled={!isEditable}
                    className="input-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, value } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: value,
                            }));
                        }
                    }}
                />
            </div>
            <div className="input-group">
                <label className="label">Past Medical/Dental History</label>
                <textarea
                    type="text"
                    name="medicalDentalHistory"
                    value={patient.medicalDentalHistory}
                    disabled={!isEditable}
                    className="input-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, value } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: value,
                            }));
                        }
                    }}
                />
            </div>
            <div className="input-group">
                <label className="label">Prior Orthodontic History</label>
                <textarea
                    name="orthodonticHistory"
                    value={patient.orthodonticHistory}
                    disabled={!isEditable}
                    className="input-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, value } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: value,
                            }));
                        }
                    }}
                />
            </div>
            <div className="checkbox-container">
                <label className="label">Under any treatment or medication now?</label>
                <input
                    type="checkbox"
                    name="currentTreatment"
                    checked={patient.currentTreatment}

                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>
            <div className="checkbox-container">
                <label className="label">Congenital Abnormalities</label>
                <input
                    type="checkbox"
                    name="congenitalAbnormalities"
                    checked={patient.congenitalAbnormalities}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>
            <div className="checkbox-container">
                <label className="label">Temporo Mandibular Joint Problems</label>
                <input
                    type="checkbox"
                    name="tmjProblems"
                    checked={patient.tmjProblems}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>
            <div className="input-group">
                <label className="label">Oral Hygiene</label>
                <select name="OralHygiene" value={patient.OralHygiene} disabled={!isEditable} className="input-field" onChange={(e) => {
                    if (isEditable) {
                        const { name, value } = e.target;
                        setPatient((prevPatient) => ({
                            ...prevPatient,
                            [name]: value,
                        }));
                    }
                }}>
                    <option value="Excellent">Excellent</option>
                    <option value="Fair">Fair</option>
                    <option value="Poor">Poor</option>
                </select>
            </div>
            <div className="input-group">
                <label className="label">Gingival Tissues</label>
                <select name="gingivalTissues" value={patient.gingivalTissues} disabled={!isEditable} className="input-field" onChange={(e) => {
                    if (isEditable) {
                        const { name, value } = e.target;
                        setPatient((prevPatient) => ({
                            ...prevPatient,
                            [name]: value,
                        }));
                    }
                }}>
                    <option value="">Select</option>
                    <option value="Thick">Thick</option>
                    <option value="Thin">Thin</option>
                    <option value="Normal">Normal</option>
                    <option value="Receding">Receding</option>
                </select>
            </div>
        </div>
    );

    const MedicalInformation = () => (
        <div>
            <h3>Please encircle the word YES/NO as the case
                may be in answering the question as follows:
            </h3>
            <h3>1. Have you ever been treated for any</h3>

            <div className="checkbox-container">
                <label className="label">Abnormal Bleeding</label>
                <input
                    type="checkbox"
                    name="congenitalAbnormalities"
                    checked={patient.congenitalAbnormalities}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Asthma</label>
                <input
                    type="checkbox"
                    name="asthma"
                    checked={patient.asthma}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Cancer</label>
                <input
                    type="checkbox"
                    name="cancer"
                    checked={patient.cancer}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Congenital Heart Defect</label>
                <input
                    type="checkbox"
                    name="congenitalHeartDefect"
                    checked={patient.congenitalHeartDefect}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Convulsions/Epilepsy</label>
                <input
                    type="checkbox"
                    name="convulsionsEpilepsy"
                    checked={patient.convulsionsEpilepsy}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Diabetes</label>
                <input
                    type="checkbox"
                    name="diabetes"
                    checked={patient.diabetes}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Handicaps/Disabilities</label>
                <input
                    type="checkbox"
                    name="handicapsDisabilities"
                    checked={patient.handicapsDisabilities}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Hearing Impairment</label>
                <input
                    type="checkbox"
                    name="hearingImpairment"
                    checked={patient.hearingImpairment}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Heart Murmur</label>
                <input
                    type="checkbox"
                    name="heartMurmur"
                    checked={patient.heartMurmur}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Hemophilia</label>
                <input
                    type="checkbox"
                    name="hemophilia"
                    checked={patient.hemophilia}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Hepatitis</label>
                <input
                    type="checkbox"
                    name="hepatitis"
                    checked={patient.hepatitis}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>

            <div className="checkbox-container">
                <label className="label">HIV +/AIDS</label>
                <input
                    type="checkbox"
                    name="hivAids"
                    checked={patient.hivAids}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Kidney/Liver Problems</label>
                <input
                    type="checkbox"
                    name="kidneyLiverProblems"
                    checked={patient.kidneyLiverProblems}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Rheumatic/Scarlet Fever</label>
                <input
                    type="checkbox"
                    name="rheumaticScarletFever"
                    checked={patient.rheumaticScarletFever}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Tuberculosis (TB)</label>
                <input
                    type="checkbox"
                    name="tuberculosis"
                    checked={patient.tuberculosis}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>

            <h3>2. Are you allergic to any drugs (penicillin),
                latex, metals, plastics, anesthetics, foods,
                materials or pollen?</h3>
            <div className="input-group">
                <label className="label">Allergies</label>
                <input
                    type="text"
                    name="allergies"
                    value={patient.allergies}
                    disabled={!isEditable}
                    className="input-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, value } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: value,
                            }));
                        }
                    }}
                />
            </div>

            <h3>3. Are you presently or have you, during the
                past two years, been under the care of a
                physician?</h3>
            <div className="checkbox-container">
                <label className="label">Under Care of Physician</label>
                <input
                    type="checkbox"
                    name="underCareOfPhysician"
                    checked={patient.underCareOfPhysician}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>

            <h3>4. Have you ever had an illness/complication
                following dental treatment of any kind?
            </h3>
            <div className="checkbox-container">
                <label className="label">Illness/Complication</label>
                <input
                    type="checkbox"
                    name="illnessComplication"
                    checked={patient.illnessComplication}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>

            <h3>5. If you are a woman,</h3>
            <div className="checkbox-container">
                <label className="label">Are you pregnant?</label>
                <input
                    type="checkbox"
                    name="isPregnant"
                    checked={patient.isPregnant}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Is your menstrual period regular</label>
                <input
                    type="checkbox"
                    name="menstrualPeriodRegular"
                    checked={patient.menstrualPeriodRegular}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>

            <h3>6. Is there any additional information about
                your general health which should be
                disclosed in confidence?</h3>
            <div className="input-group">
                <label className="label">Additional Health Information</label>
                <textarea
                    type="text"
                    name="additionalHealthInfo"
                    value={patient.additionalHealthInfo}
                    disabled={!isEditable}
                    className="input-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, value } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: value,
                            }));
                        }
                    }}
                />
            </div>

            <h3>Oral Habits</h3>
            <div className="checkbox-container">
                <label className="label">Clenching/Grinding Teeth</label>
                <input
                    type="checkbox"
                    name="clenchingGrindingTeeth"
                    checked={patient.clenchingGrindingTeeth}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Lip Sucking/Biting</label>
                <input
                    type="checkbox"
                    name="lipSuckingBiting"
                    checked={patient.lipSuckingBiting}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Mouth Breather</label>
                <input
                    type="checkbox"
                    name="mouthBreather"
                    checked={patient.mouthBreather}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Musical Instrument</label>
                <input
                    type="checkbox"
                    name="musicalInstrument"
                    checked={patient.musicalInstrument}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Nail Biting</label>
                <input
                    type="checkbox"
                    name="nailBiting"
                    checked={patient.nailBiting}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Speech Problems</label>
                <input
                    type="checkbox"
                    name="speechProblems"
                    checked={patient.speechProblems}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Nursing Bottle Habits</label>
                <input
                    type="checkbox"
                    name="nursingBottleHabits"
                    checked={patient.nursingBottleHabits}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Thumb/Finger Sucking</label>
                <input
                    type="checkbox"
                    name="thumbFingerSucking"
                    checked={patient.thumbFingerSucking}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Tongue Thrusting</label>
                <input
                    type="checkbox"
                    name="tongueThrusting"
                    checked={patient.tongueThrusting}
                    className="checkbox-field"
                    onChange={(e) => {
                        if (isEditable) {
                            const { name, checked } = e.target;
                            setPatient((prevPatient) => ({
                                ...prevPatient,
                                [name]: checked,
                            }));
                        }
                    }}
                />
            </div>
        </div>
    );

    const columns = [
        {
            name: 'Date',
            selector: row => format(new Date(row.date), 'MM/dd/yyyy'),
            sortable: true,
        },
        {
            name: 'Time',
            selector: row => `${row.startTime} - ${row.endTime}`,
            sortable: true,
        },
        {
            name: 'Reason',
            selector: 'reason',
            sortable: true,
        },
        {
            name: 'Dentist',
            selector: 'dentist',
            sortable: true,
        },
        {
            name: 'Status',
            selector: 'status',
            sortable: true,
            cell: row => (
                <span className={`status-badge ${row.status.toLowerCase()}`}>
                    {row.status}
                </span>
            ),
        },
    ];

    const customStyles = {
        rows: {
            style: {
                borderBottom: '1px solid #ddd',
            },
        },
        headCells: {
            style: {
                backgroundColor: '#007bff',
                color: 'white',
                fontWeight: 'bold',
                padding: '15px 10px',
                textAlign: 'center',
            },
        },
        cells: {
            style: {
                padding: '12px 10px',
                borderRight: '1px solid #ddd',
            },
        },
        pagination: {
            style: {
                display: 'flex',
                justifyContent: 'center',
                padding: '10px',
                backgroundColor: '#f4f4f4',
            },
        },
        sortIcon: {
            style: {
                color: '#007bff',
                fontSize: '18px',
            },
        },
    };

    const Appointments = () => (
        <div className="appointments-container">
        <h2>Appointments</h2>
        {appointments.length === 0 ? (
            <p>No appointments found.</p>
        ) : (
            <DataTable
                columns={columns}
                data={appointments}
                keyField="_id"
                pagination
                highlightOnHover
                striped
                responsive
                customStyles={customStyles}
                defaultSortFieldId={1}
                defaultSortAsc={true}
            />
        )}
    </div>
    );

    const DentalChartTab = () => (
        <div>
            <DentalChart />
        </div>
    );

    const Notes = () => (
        <div>
            <h2>Notes</h2>
            <textarea
                name="Notes"
                className="prescription-input"
                placeholder="Write Notes here..."
                value={patient.Notes || ''}
                disabled={!isEditable}
                onChange={(e) => {
                    if (isEditable) {
                        const { name, value } = e.target;
                        setPatient((prevPatient) => ({
                            ...prevPatient,
                            [name]: value,
                        }));
                    }
                }}
            />
        </div>
    );

    const MedicalAlert = () => (
        <div>
            <h2>Medical Alert</h2>
            <textarea
                name="MedicalAlert"
                className="prescription-input"
                placeholder="Write Medical Alert here..."
                value={patient.MedicalAlert}
                disabled={!isEditable}
                onChange={(e) => {
                    if (isEditable) {
                        const { name, value } = e.target;
                        setPatient((prevPatient) => ({
                            ...prevPatient,
                            [name]: value,
                        }));
                    }
                }}
            />
        </div>
    );

    const Settings = () => {
        return (
            <div>
                <h2>Settings</h2>
                <select
                    name="userAccount"
                    value={patient.userAccount || ''}
                    disabled={!isEditable}
                    className="input-field"
                    onChange={(e) => {
                        if (isEditable) {
                            setPatient((prev) => ({
                                ...prev,
                                userAccount: e.target.value
                            }));
                        }
                    }}
                >
                    <option value="">Select a user</option>
                    {users
                        .filter(user =>
                            user.type === 'External' &&
                            (!user.patientID || user.patientID === patient.patientID)
                        )
                        .map((user) => (
                            <option key={user._id} value={user._id}>
                                {`${user.firstName} ${user.lastName} (${user.email})`}
                                {user.patientID && user.patientID === patient.patientID ? " (Current)" : ""}
                            </option>
                        ))}
                </select>
            </div>
        );
    };

    // Render active tab content
    const renderActiveTabContent = () => {
        switch (activeTab) {
            case 'GeneralInfo':
                return <GeneralInfo />
            case 'MedicalInformation':
                return <MedicalInformation />;
            case 'appointments':
                return <Appointments />;
            case 'dentalChart':
                return <DentalChartTab />;
            case 'Notes':
                return <Notes />;
            case 'MedicalAlert':
                return <MedicalAlert />;
            case 'Settings':
                return <Settings />;
            default:
                return <GeneralInfo />
        }
    };

    return (
        <AccessControl>
            <div className={`create-patient-container ${isSidebarVisible ? 'sidebar-visible' : 'sidebar-hidden'}`}>
                <InternalHeader moduleName="Patients" toggleSidebar={toggleSidebar} />
                <div className="create-patient-content">
                    <Sidebar isSidebarVisible={isSidebarVisible} />
                    <div className="appointments-container2">
                        <LinkMap
                            links={linkData}
                            moduleName="Patients"
                            moduleIcon="patients"
                        />
                        <div className='create-patient'>
                            <div>
                                {!isEditable ? (
                                    <button className="create-patient-button" onClick={handleEditClick}>Edit</button>
                                ) : (
                                    <button className="create-patient-button" onClick={handleSaveClick}>Save</button>
                                )}
                            </div>
                            <div className="create-patient-form">
                                <h2>Patient Profile</h2>
                                <div className="input-group">
                                    <label className="label">Patient ID</label>
                                    <input type="text" name="name" value={patient.patientID} disabled className="input-field" />
                                </div>
                                <div className="input-group">
                                    <label className="label">Name</label>
                                    <input type="text" name="name" value={patient.name} disabled={!isEditable} className="input-field" onChange={(e) => {
                                        if (isEditable) {
                                            const { name, value } = e.target;
                                            setPatient((prevPatient) => ({
                                                ...prevPatient,
                                                [name]: value,
                                            }));
                                        }
                                    }} />
                                </div>
                                <div className="input-group">
                                    <label className="label">Sex</label>
                                    <select name="sex" value={patient.sex} disabled={!isEditable} className="input-field" onChange={(e) => {
                                        if (isEditable) {
                                            const { name, value } = e.target;
                                            setPatient((prevPatient) => ({
                                                ...prevPatient,
                                                [name]: value,
                                            }));
                                        }
                                    }}>
                                        <option value="">Select</option>
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                    </select>
                                </div>
                                <div className="input-group">
                                    <label className="label">Date of Birth</label>
                                    <input
                                        type="date"
                                        name="birthdate"
                                        value={patient.birthdate ? new Date(patient.birthdate).toISOString().split('T')[0] : ''}
                                        className="input-field"
                                        disabled={!isEditable}
                                        onChange={(e) => {
                                            if (isEditable) {
                                                const { name, value } = e.target;
                                                const birth = new Date(value);
                                                const now = new Date();

                                                let years = now.getFullYear() - birth.getFullYear();
                                                let months = now.getMonth() - birth.getMonth();

                                                if (months < 0 || (months === 0 && now.getDate() < birth.getDate())) {
                                                    years--;
                                                    months += 12;
                                                }

                                                setPatient((prevPatient) => ({
                                                    ...prevPatient,
                                                    [name]: value,
                                                    ageYear: years,
                                                    ageMonth: months,
                                                }));
                                            }
                                        }}
                                    />
                                </div>
                                <div className="input-group">
                                    <label className="label">Age</label>
                                    <div className="age-inputs">
                                        <input
                                            type="number"
                                            name="ageYear"
                                            value={patient.ageYear}
                                            disabled
                                            className="age-input"
                                        />
                                        <span className="age-label">Years</span>
                                        <input
                                            type="number"
                                            name="ageMonth"
                                            value={patient.ageMonth}
                                            disabled
                                            className="age-input"
                                        />
                                        <span className="age-label">Months</span>
                                    </div>
                                </div>
                                {(parseInt(patient.ageYear, 10) < 18) && (
                                    <div className="input-group">
                                        <label className="label">Parent's/Guardian's Name</label>
                                        <input
                                            type="text"
                                            name="guardianName"
                                            value={patient.guardianName}
                                            className="input-field"
                                            disabled={!isEditable}
                                            onChange={(e) => {
                                                if (isEditable) {
                                                    const { name, value } = e.target;
                                                    setPatient((prevPatient) => ({
                                                        ...prevPatient,
                                                        [name]: value,
                                                    }));
                                                }
                                            }}
                                        />
                                    </div>
                                )}
                                <div className="input-group">
                                    <label className="label">Occupation</label>
                                    <input type="text" name="occupation" value={patient.occupation} disabled={!isEditable} className="input-field" onChange={(e) => {
                                        if (isEditable) {
                                            const { name, value } = e.target;
                                            setPatient((prevPatient) => ({
                                                ...prevPatient,
                                                [name]: value,
                                            }));
                                        }
                                    }} />
                                </div>
                                <div className="input-group">
                                    <label className="label">Address</label>
                                    <input type="text" name="address" value={patient.address} disabled={!isEditable} className="input-field" onChange={(e) => {
                                        if (isEditable) {
                                            const { name, value } = e.target;
                                            setPatient((prevPatient) => ({
                                                ...prevPatient,
                                                [name]: value,
                                            }));
                                        }
                                    }} />
                                </div>
                                <div className="input-group">
                                    <label className="label">Contact Number</label>
                                    <input type="text" name="ContactInformation" value={patient.ContactInformation} disabled={!isEditable} className="input-field" onChange={(e) => {
                                        if (isEditable) {
                                            const { name, value } = e.target;
                                            setPatient((prevPatient) => ({
                                                ...prevPatient,
                                                [name]: value,
                                            }));
                                        }
                                    }} />
                                </div>
                                <div className="input-group">
                                    <label className="label">Email</label>
                                    <input type="text" name="ContactInformation" value={patient.email} disabled={!isEditable} className="input-field" onChange={(e) => {
                                        if (isEditable) {
                                            const { name, value } = e.target;
                                            setPatient((prevPatient) => ({
                                                ...prevPatient,
                                                [name]: value,
                                            }));
                                        }
                                    }} />
                                </div>
                                <br />
                                {/* Tab Navigation */}
                                <div className="tab-navigation">
                                    <span
                                        onClick={() => setActiveTab('GeneralInfo')}
                                        className={`tab ${activeTab === 'GeneralInfo' ? 'active' : ''}`}
                                    >
                                        General Information
                                    </span>
                                    <span
                                        onClick={() => setActiveTab('MedicalInformation')}
                                        className={`tab ${activeTab === 'MedicalInformation' ? 'active' : ''}`}
                                    >
                                        Medical Information
                                    </span>
                                    <span
                                        onClick={() => setActiveTab('appointments')}
                                        className={`tab ${activeTab === 'appointments' ? 'active' : ''}`}
                                    >
                                        Appointments
                                    </span>
                                    <span
                                        onClick={() => setActiveTab('dentalChart')}
                                        className={`tab ${activeTab === 'dentalChart' ? 'active' : ''}`}
                                    >
                                        Dental Chart
                                    </span>
                                    <span
                                        onClick={() => setActiveTab('Notes')}
                                        className={`tab ${activeTab === 'Notes' ? 'active' : ''}`}
                                    >
                                        Notes
                                    </span>
                                    <span
                                        onClick={() => setActiveTab('MedicalAlert')}
                                        className={`tab ${activeTab === 'MedicalAlert' ? 'active' : ''}`}
                                    >
                                        Medical Alert
                                    </span>
                                    <span
                                        onClick={() => setActiveTab('Settings')}
                                        className={`tab ${activeTab === 'Settings' ? 'active' : ''}`}
                                    >
                                        Settings
                                    </span>
                                </div>
                                {/* Render the active tab content within the same page */}
                                <div className="tab-content">
                                    {renderActiveTabContent()}
                                </div>

                                {successModalVisible && (
                                    <div className="modal-overlay" onClick={handleOverlayClick}>
                                        <div className="modal success-modal" onClick={(e) => e.stopPropagation()}>
                                            <h3>Sucess!</h3>
                                            <div class="modal-divider" />
                                            <p>Patient profile updated successfully.</p>
                                            <div class="modal-divider" />
                                            <button className="confirm-button" onClick={closeSuccessModal}>Close</button>
                                        </div>
                                    </div>
                                )}

                                {medicalAlertModalVisible && (
                                    <div className="modal-overlay" onClick={() => setMedicalAlertModalVisible(false)}>
                                        <div className="modal warning-modal" onClick={(e) => e.stopPropagation()}>
                                            <h3>⚠️ Medical Alert</h3>
                                            <div className="modal-divider" />
                                            <p>{patient.MedicalAlert}</p>
                                            <div className="modal-divider" />
                                            <button className="confirm-button" onClick={() => setMedicalAlertModalVisible(false)}>
                                                Close
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AccessControl>
    );
};

export default PatientProfile;